import JSZip from "jszip";

export const SINGLE_FILE_LIMIT_BYTES = 50 * 1024 * 1024;   // 50 MB
export const TOTAL_SIZE_LIMIT_BYTES  = 200 * 1024 * 1024;  // 200 MB

export interface DownloadableItem {
  id: number | string;
  name: string;
  blobId: string;
}

export interface SizeCheckResult {
  acceptedItems: DownloadableItem[];
  skippedItems: DownloadableItem[];   // individually exceed 50 MB
  totalKnownBytes: number;
  totalExceedsLimit: boolean;         // accepted total > 200 MB
}

export async function checkFileSizes(
  items: DownloadableItem[],
  endpointBase: string,
  getAccessToken: () => Promise<string>
): Promise<SizeCheckResult> {
  const accessToken = await getAccessToken();

  const withSizes = await Promise.all(
    items.map(async (item) => {
      try {
        const res = await fetch(`${endpointBase}${item.blobId}`, {
          method: "HEAD",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const len = res.headers.get("Content-Length");
        return { item, sizeBytes: len ? parseInt(len, 10) : null };
      } catch {
        return { item, sizeBytes: null };
      }
    })
  );

  const skippedItems = withSizes
    .filter((x) => x.sizeBytes !== null && x.sizeBytes > SINGLE_FILE_LIMIT_BYTES)
    .map((x) => x.item);

  const acceptedItems = withSizes
    .filter((x) => x.sizeBytes === null || x.sizeBytes <= SINGLE_FILE_LIMIT_BYTES)
    .map((x) => x.item);

  const totalKnownBytes = withSizes
    .filter((x) => acceptedItems.some((a) => a.id === x.item.id) && x.sizeBytes !== null)
    .reduce((sum, x) => sum + (x.sizeBytes ?? 0), 0);

  return {
    acceptedItems,
    skippedItems,
    totalKnownBytes,
    totalExceedsLimit: totalKnownBytes > TOTAL_SIZE_LIMIT_BYTES,
  };
}

export async function downloadAsZip(
  items: DownloadableItem[],
  endpointBase: string,
  getAccessToken: () => Promise<string>,
  zipFileName: string
): Promise<{ succeeded: DownloadableItem[]; failed: DownloadableItem[] }> {
  const accessToken = await getAccessToken();
  const succeeded: DownloadableItem[] = [];
  const failed: DownloadableItem[] = [];

  const results = await Promise.allSettled(
    items.map(async (item) => {
      const res = await fetch(`${endpointBase}${item.blobId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return { item, blob: await res.blob() };
    })
  );

  // Single file → direct download, no zip wrapper
  if (items.length === 1) {
    const r = results[0];
    if (r.status === "fulfilled") {
      triggerBlobDownload(r.value.blob, items[0].name);
      succeeded.push(items[0]);
    } else {
      failed.push(items[0]);
    }
    return { succeeded, failed };
  }

  // Multiple files → bundle into zip
  const zip = new JSZip();
  results.forEach((r, i) => {
    if (r.status === "fulfilled") {
      zip.file(r.value.item.name, r.value.blob);
      succeeded.push(items[i]);
    } else {
      failed.push(items[i]);
    }
  });

  if (succeeded.length > 0) {
    const zipBlob = await zip.generateAsync({ type: "blob" });
    triggerBlobDownload(zipBlob, zipFileName);
  }

  return { succeeded, failed };
}

// Sequential individual downloads with delay to avoid browser popup blocking
export async function downloadFilesIndividually(
  items: DownloadableItem[],
  endpointBase: string,
  getAccessToken: () => Promise<string>
): Promise<void> {
  for (let i = 0; i < items.length; i++) {
    if (i > 0) await new Promise<void>((r) => setTimeout(r, 700));
    try {
      const accessToken = await getAccessToken();
      const res = await fetch(`${endpointBase}${items[i].blobId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) triggerBlobDownload(await res.blob(), items[i].name);
    } catch {
      console.error(`Individual download failed: ${items[i].name}`);
    }
  }
}

export function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", fileName);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
